import React from 'react';
import { Wifi, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageConfig } from '../pages';

interface TopBarProps {
  config: PageConfig;
}

export const TopBar: React.FC<TopBarProps> = ({ config }) => {
  if (!config.actions?.length && !config.connectionStatus && !config.headerComponent) {
    return null;
  }

  return (
    <div className="bg-white border-b border-slate-200 px-4 py-3 mb-4 md:px-6 md:py-4 md:mb-6 rounded-xl shadow-sm print:hidden">
      <div className="flex justify-end gap-4">
        {/* Custom Header Component (filters, search, etc) */}
        {config.headerComponent && (
          <div className="flex-1">
            {config.headerComponent}
          </div>
        )}


        {/* Action buttons and connection status */}
        <div className="flex flex-wrap items-center gap-3">
            {/* Connection Status Indicator */}
            {config.connectionStatus && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200">
                {config.connectionStatus === 'connected' ? (
                  <>
                    <Wifi className="text-green-600 h-4 w-4" />
                    <span className="text-sm text-slate-700">Conectado</span>
                  </>
                ) : config.connectionStatus === 'connecting' ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-300 border-t-slate-600"></div>
                    <span className="text-sm text-slate-600">Conectando...</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="text-slate-400 h-4 w-4" />
                    <span className="text-sm text-slate-500">Sin conexión</span>
                  </>
                )}
              </div>
            )}
            
            {/* Action Buttons */}
            {config.actions && config.actions.map((action, index) => {
              const Icon = action.icon;
              
              // Map legacy variants to Shadcn variants
              const getVariant = (v?: string): "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" => {
                switch(v) {
                  case 'primary': return 'default';
                  case 'danger': return 'destructive';
                  case 'secondary': default: return 'outline';
                }
              };

              const labelClass = action.iconOnlyOnTablet ? "hidden xl:inline" : "hidden md:inline";
              const buttonClass = action.iconOnly
                ? ""
                : action.iconOnlyOnTablet
                  ? "size-9 px-0 has-[>svg]:px-0 xl:w-auto xl:px-4 xl:has-[>svg]:px-3 xl:min-w-[100px]"
                  : "min-w-[100px]";
              const iconMarginClass = action.iconOnly
                ? ""
                : action.iconOnlyOnTablet
                  ? "xl:mr-2"
                  : "mr-2";
              return (
                <Button
                  key={index}
                  onClick={action.onClick}
                  variant={getVariant(action.variant)}
                  size={action.iconOnly ? "icon" : "default"}
                  className={buttonClass}
                  title={action.tooltip || action.label}
                >
                  {typeof Icon === 'string' ? null : <Icon size={18} className={iconMarginClass} />}
                  {!action.iconOnly && <span className={labelClass}>{action.label}</span>}
                </Button>
              );
            })}
          </div>
      </div>
    </div>
  );
};
